<script setup>
import { computed } from 'vue'

/**
 * One product a tutor can choose to offer.
 *
 * Three things were wrong with how it drew, all visible with the two cards side
 * by side:
 *
 *  - **The card centred its own contents.** A `<button>` centres what is inside
 *    it vertically unless told otherwise, so on the shorter card the icon and
 *    the title floated to the middle while its taller twin started at the top.
 *    It is a column now, packed to the top.
 *  - **Each product painted its own border colour**, taken from a per-offering
 *    value, so one card outlined itself green and the other purple — two hues
 *    that appear nowhere else on the page. A selected card is brand-coloured,
 *    like every other selected thing on the site.
 *  - **"Coming soon" was amber**, which is the colour this site uses for
 *    something being wrong. A product that has not launched yet is news, not a
 *    warning.
 */
const props = defineProps({
  offering: {
    type: Object,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['toggle'])

const cardClasses = computed(() => [
  'flex h-full flex-col rounded-2xl border-2 p-6 text-left transition-all duration-200 ease-brand cursor-pointer',
  props.selected
    ? 'border-brand bg-brand-soft shadow-md'
    : 'border-line bg-surface hover:border-line-brand hover:shadow-lg',
])

const handleToggle = () => {
  emit('toggle', props.offering.key)
}
</script>

<template>
  <button type="button" :class="cardClasses" @click="handleToggle">
    <div class="mb-4 flex items-start justify-between gap-3">
      <span class="grid size-16 shrink-0 place-items-center rounded-2xl bg-surface-2">
        <span class="material-symbols-outlined text-2xl text-brand">{{ offering.icon_name }}</span>
      </span>

      <span
        class="grid size-7 shrink-0 place-items-center rounded-full border-2 transition-colors"
        :class="selected ? 'border-brand bg-brand text-brand-contrast' : 'border-line-strong text-subtle'"
      >
        <span class="material-symbols-outlined text-sm">{{ selected ? 'check' : 'add' }}</span>
      </span>
    </div>

    <div class="mb-2 flex flex-wrap items-center gap-2">
      <h3 class="text-lg font-bold text-ink">{{ offering.name }}</h3>
      <span
        v-if="offering.coming_soon"
        class="rounded-full bg-live-soft px-2.5 py-1 text-xs font-semibold text-live-soft-text"
      >
        Coming soon
      </span>
    </div>

    <p class="mb-4 text-sm text-muted">{{ offering.description }}</p>

    <div v-if="offering.badges?.length" class="flex flex-wrap items-center gap-2">
      <span
        v-for="(badge, index) in offering.badges"
        :key="index"
        class="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors"
        :class="selected ? 'bg-surface text-ink' : 'bg-surface-2 text-muted'"
      >
        <span class="material-symbols-outlined text-sm">{{ badge.icon }}</span>
        {{ badge.label }}
      </span>
    </div>

    <!--
      What selecting a product that has not launched actually commits the tutor
      to, said on the card rather than left for them to guess. `mt-auto` pins it
      to the bottom so the note does not float up the middle of a short card.
    -->
    <p
      v-if="offering.coming_soon && offering.coming_soon_note"
      class="mt-4 rounded-xl bg-live-soft p-3 text-xs text-live-soft-text"
    >
      {{ offering.coming_soon_note }}
    </p>
  </button>
</template>
